import {
  Button,
  LoadingIndicator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import { useContext, useMemo } from 'react';
import { useContentLibraryOptions } from '../../hooks/useContentLibraryOptions';
import { useFormFieldOptions } from '../../hooks/useFormFieldOptions';
import { FormContext } from '../../hooks/FormContext';
import {
  findMissingRequiredContentLibraryInput,
  hasMissingRequiredContentLibraryInputs,
} from '../../utils/context-requirements';
import { isRuleActive } from '../../utils/validation';
import type { DataSourceConfig, SelectOption, ValidationRules } from '@commenda/types';
import { LabelWithGuidanceTooltip } from './LabelWithGuidanceTooltip';

export const SelectComponent = ({
  fieldId,
  label,
  placeholder,
  options: staticOptions,
  guidance,
  description,
  validation,
  dataSource,
  props = {},
}: {
  fieldId: string;
  label?: string;
  placeholder?: string;
  options?: SelectOption[];
  guidance?: { text?: string };
  description?: string;
  validation?: {
    rules?: ValidationRules;
    triggers?: string[];
  };
  dataSource?: DataSourceConfig;
  props?: Record<string, unknown>;
}) => {
  const {
    formData,
    updateField,
    errors,
    validateField,
    isFieldDisabled,
    isFieldVisible,
    dslContext,
  } = useContext(FormContext);

  const isDynamicDataSource =
    dataSource?.type === 'content-library' && dataSource.contentLibrary;

  const isFormFieldDataSource =
    dataSource?.type === 'form-field' && !!dataSource.formField;

  const { options: formFieldOptions } = useFormFieldOptions({
    config: dataSource?.formField,
    formData,
    skip: !isFormFieldDataSource,
  });

  const {
    options: dynamicOptions,
    isLoading,
    isLoadingMore,
    error: optionsError,
    hasMore,
    loadMore,
  } = useContentLibraryOptions({
    config: dataSource?.contentLibrary,
    dslContext,
    formData,
    skip: !isDynamicDataSource,
  });

  const hasMissingInputs = useMemo(() => {
    if (!isDynamicDataSource) {
      return false;
    }
    return hasMissingRequiredContentLibraryInputs(
      dataSource?.contentLibrary?.inputs,
      dslContext,
      formData
    );
  }, [
    isDynamicDataSource,
    dataSource?.contentLibrary?.inputs,
    dslContext,
    formData,
  ]);

  const options = isFormFieldDataSource
    ? formFieldOptions
    : isDynamicDataSource
      ? dynamicOptions
      : staticOptions;
  const rawValue = formData[fieldId];
  const value = rawValue != null ? String(rawValue) : '';
  const disabled =
    props.disabled || isFieldDisabled(fieldId) || hasMissingInputs;
  const selectedOption = options?.find((option) => option.value === value);
  const isRequired = isRuleActive(validation?.rules?.required, isFieldVisible);

  const handleChange = (val: string) => {
    updateField(fieldId, val);
    validateField(fieldId, val, validation?.rules);
  };

  const getPlaceholder = () => {
    if (hasMissingInputs) {
      const missingInput = findMissingRequiredContentLibraryInput(
        dataSource?.contentLibrary?.inputs,
        dslContext,
        formData
      );
      if (missingInput?.source === 'field' && missingInput.fieldId) {
        return `Select ${missingInput.fieldId} first`;
      }
      if (missingInput?.source === 'context' && missingInput.contextPath) {
        return `Waiting for ${missingInput.contextPath}...`;
      }
    }
    if (isLoading) {
      return 'Loading options...';
    }
    return placeholder || 'Select an option';
  };

  return (
    <div className="mb-4" data-field-id={fieldId}>
      {(label || description) && (
        <LabelWithGuidanceTooltip
          label={label}
          guidance={guidance?.text}
          isRequired={isRequired}
          description={description}
        />
      )}
      <Select
        value={value}
        onValueChange={handleChange}
        disabled={disabled as boolean}
      >
        <SelectTrigger
          error={
            errors[fieldId] || (optionsError ? optionsError.message : undefined)
          }
        >
          <div className="flex items-center gap-2">
            {isLoading && <LoadingIndicator size={16} />}
            <SelectValue placeholder={getPlaceholder()} />
          </div>
        </SelectTrigger>
        <SelectContent>
          {options?.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </SelectItem>
          ))}
          {(isDynamicDataSource || isFormFieldDataSource) &&
            options?.length === 0 &&
            !isLoading &&
            !optionsError && (
              <div className="px-3 py-2 text-text-tertiary txt-small-regular">
                No options available
              </div>
            )}
          {isDynamicDataSource && hasMore && (
            <div className="px-2 py-1 border-t border-border-secondary">
              <Button
                type="button"
                variant="tertiary"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  loadMore();
                }}
                disabled={isLoadingMore}
                className="w-full"
              >
                {isLoadingMore ? (
                  <div className="flex items-center gap-2">
                    <LoadingIndicator size={16} />
                    <span>Loading...</span>
                  </div>
                ) : (
                  'Load More'
                )}
              </Button>
            </div>
          )}
        </SelectContent>
      </Select>
      {selectedOption?.helpText && (
        <p className="txt-small-regular text-text-tertiary mt-2">
          {selectedOption.helpText}
        </p>
      )}
      {optionsError && (
        <p className="mt-1 text-sm text-text-error">{optionsError.message}</p>
      )}
      {errors[fieldId] && (
        <p className="mt-1 text-sm text-text-error">{errors[fieldId]}</p>
      )}
    </div>
  );
};
